import { useState } from 'react';

import { Badge } from '@/ui';

/** Past this the badge says "99+": a rail is no place for a four-digit number. */
const COUNT_CEILING = 99;

/**
 * The number painted on the badge.
 *
 * @param total how many things are waiting
 * @returns the count, or "99+" past the ceiling
 */
export function countLabel(total: number): string {
  return total > COUNT_CEILING ? `${String(COUNT_CEILING)}+` : String(total);
}

/**
 * What a screen reader is told when the count changes.
 *
 * @param total how many things are waiting now
 * @returns a short sentence that leads with the place the count belongs to
 */
export function announcementFor(total: number): string {
  return total === 0 ? 'Today: nothing is waiting' : `Today: ${String(total)} waiting`;
}

export interface TodayRailBadgeProps {
  /** The id the link's name points at, so the count is part of it. */
  readonly id: string;
  /** How many things are waiting; undefined while it is unknown. */
  readonly total: number | undefined;
}

/**
 * The count that rides on the Today link.
 *
 * The badge is drawn only when something is waiting, so "0" and "unknown" look the same as a rail
 * with nothing to say. The holder it sits in is always there, because the link's name points at it
 * by id. The word "waiting" follows the number out of sight: the link is named by every word it
 * paints (WCAG 2.5.3), and a name of "Today 12 waiting" also says what the 12 is.
 *
 * @param props see {@link TodayRailBadgeProps}
 * @returns the holder, with the badge in it when something is waiting
 */
export function TodayRailBadge({ id, total }: TodayRailBadgeProps): React.JSX.Element {
  return (
    <span className="fo-rail__count" id={id}>
      {total === undefined || total <= 0 ? null : (
        <Badge tone="accent">
          {countLabel(total)}
          {/* The space is outside the hidden word: a name trims the text inside a nested element,
              so a space in there would let "12" and "waiting" run together. */}{' '}
          <span className="visually-hidden">waiting</span>
        </Badge>
      )}
    </span>
  );
}

export interface TodayRailAnnouncerProps {
  /** How many things are waiting; undefined while it is unknown. */
  readonly total: number | undefined;
}

/**
 * Says, politely, when the count changes.
 *
 * It sits beside the link rather than inside it, so a change is read out without being read as part
 * of the link, and it stays quiet for the first count it learns: a person opening the app should
 * hear nothing until something changes. It has no `status` role on purpose; that role is the
 * shell's one place for its own messages.
 *
 * @param props see {@link TodayRailAnnouncerProps}
 * @returns the live region
 */
export function TodayRailAnnouncer({ total }: TodayRailAnnouncerProps): React.JSX.Element {
  const [seen, setSeen] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  // Compared with the last count during render, the way React asks for a value derived from the
  // previous one, so the message and the badge are never out of step for a frame.
  if (total !== undefined && total !== seen) {
    setSeen(total);
    if (seen !== null) {
      setMessage(announcementFor(total));
    }
  }
  return (
    <div
      className="visually-hidden"
      aria-live="polite"
      aria-atomic="true"
      data-testid="today-count-announcer"
    >
      {message}
    </div>
  );
}
